import { useContext, useId, type ReactNode } from "react";
import type { FactsField, FactsItemField, FactsProblem } from "../../lib/facts/types";
import { DAY_LABEL, formatNumber } from "../../lib/facts/words";
import {
  emptyRow,
  type DayInput,
  type FieldInput,
  type ItemInput,
  type ListRow,
  type PriceInput
} from "../../lib/facts/inputs";
import { ProblemSentence } from "./problemSentence";

/**
 * One control per field type for a facts form, build round 18 (2026-10-07). The form holds its state
 * and says which problems belong where; these draw a field, its label and help from the schema as
 * text, its hint, and the problems at its own path.
 *
 * A problem's path is the contract's: the field's key, `hours[0]` for a day, `staff[1].name` inside a
 * list row. Each control asks `problemsAt` for its own, so a problem at a path no control draws is the
 * form's to list apart.
 *
 * Moved here from `app/routes/adminClient/SiteFactsControls.tsx` in build round 19 (2026-10-08), when
 * the client's Webbplatsens fakta began to draw the same controls. Petrovic's Fakta sheet and the
 * client's form are one set of controls, so a field looks and converts the same on both sides. The
 * one thing that differs is the words a problem is said in, which a form may set with
 * `ProblemSentence` (`./problemSentence.ts`): the client is never told about a schema.
 */

export type ProblemsAt = (path: string) => FactsProblem[];

/** The hint under a control: the schema's help, then what the type allows, then whether it is required. */
function hintFor(field: FactsField | FactsItemField): string {
  const parts: string[] = [];
  if (field.help) {
    parts.push(field.help);
  }
  switch (field.type) {
    case "text":
      if (!field.allowed_values && field.max_length) {
        parts.push(`Högst ${field.max_length} tecken.`);
      }
      break;
    case "number":
      if (field.min !== undefined && field.max !== undefined) {
        parts.push(`Från ${formatNumber(field.min)} till ${formatNumber(field.max)}${field.integer ? ", heltal" : ""}.`);
      }
      break;
    case "price":
      if (field.max !== undefined) {
        parts.push(`Högst ${formatNumber(field.max)} kronor, högst två decimaler.`);
      }
      break;
    case "phone":
      parts.push("Sparas som +46 och numret utan nolla.");
      break;
    case "url":
      parts.push("Hela adressen, med https://.");
      break;
    case "opening_hours":
      parts.push("Lämna alla tider tomma om webbplatsen inte ska visa några öppettider.");
      break;
    case "list":
      if (field.min_items !== undefined && field.max_items !== undefined) {
        parts.push(`Från ${field.min_items} till ${field.max_items} rader.`);
      }
      break;
  }
  if (field.required) {
    parts.push("Måste fyllas i.");
  }
  return parts.join(" ");
}

function ProblemLine({
  id,
  path,
  problems,
  field
}: {
  id: string;
  path: string;
  problems: FactsProblem[];
  field?: FactsField | FactsItemField;
}) {
  const sentence = useContext(ProblemSentence);
  if (problems.length === 0) {
    return null;
  }
  return (
    <p className="form-error" id={id} data-problem-key={path}>
      {problems.map((problem) => sentence(problem.code, field)).join(" ")}
    </p>
  );
}

/** One text, number, phone, e-mail or address field, or one price, at the top level or in a list row. */
function ValueControl({
  field,
  input,
  path,
  problemsAt,
  onChange
}: {
  field: FactsField | FactsItemField;
  input: ItemInput;
  path: string;
  problemsAt: ProblemsAt;
  onChange: (next: ItemInput) => void;
}) {
  const id = useId();
  const hint = hintFor(field);
  const problems = problemsAt(path);
  const describedBy = [hint ? `${id}-hint` : "", problems.length ? `${id}-problem` : ""].filter(Boolean).join(" ") || undefined;
  const footer = (
    <>
      {hint ? (
        <p className="line-hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
      <ProblemLine id={`${id}-problem`} path={path} problems={problems} field={field} />
    </>
  );

  if (field.type === "price") {
    const price: PriceInput = input.kind === "price" ? input : { kind: "price", amount: "", from: false, incl_vat: false };
    return (
      <fieldset className="box-field" data-testid="facts-field" data-key={path} aria-describedby={describedBy}>
        <legend>{field.label}</legend>
        <div className="box-field">
          <label htmlFor={`${id}-amount`}>Belopp i kronor</label>
          <input
            id={`${id}-amount`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={price.amount}
            aria-invalid={problems.length > 0 || undefined}
            onChange={(event) => onChange({ ...price, amount: event.target.value })}
          />
        </div>
        {field.from?.editable === false ? (
          <span className="quiet">{field.from.value ? "Alltid ett från-pris." : "Aldrig ett från-pris."}</span>
        ) : (
          <label className="box-check">
            <input
              type="checkbox"
              checked={price.from}
              onChange={(event) => onChange({ ...price, from: event.target.checked })}
            />
            Från-pris
          </label>
        )}
        {field.incl_vat?.editable === false ? (
          <span className="quiet">{field.incl_vat.value ? "Alltid inklusive moms." : "Alltid exklusive moms."}</span>
        ) : (
          <label className="box-check">
            <input
              type="checkbox"
              checked={price.incl_vat}
              onChange={(event) => onChange({ ...price, incl_vat: event.target.checked })}
            />
            Inklusive moms
          </label>
        )}
        {footer}
      </fieldset>
    );
  }

  const text = input.kind === "scalar" ? input.text : "";
  const change = (value: string) => onChange({ kind: "scalar", text: value });
  const common = {
    id,
    value: text,
    autoComplete: "off",
    "aria-invalid": problems.length > 0 || undefined,
    "aria-describedby": describedBy
  };

  let control: ReactNode;
  if (field.type === "text" && field.allowed_values) {
    // A stored value the schema no longer allows is still offered, so the form shows what is saved.
    const options = text !== "" && !field.allowed_values.includes(text) ? [...field.allowed_values, text] : field.allowed_values;
    control = (
      <select {...common} onChange={(event) => change(event.target.value)}>
        <option value="">Inget värde</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  } else {
    const kind =
      field.type === "number"
        ? { type: "text", inputMode: field.integer ? ("numeric" as const) : ("decimal" as const) }
        : field.type === "phone"
          ? { type: "tel" }
          : field.type === "email"
            ? { type: "email", spellCheck: false }
            : field.type === "url"
              ? { type: "url", spellCheck: false }
              : { type: "text" };
    control = <input {...common} {...kind} onChange={(event) => change(event.target.value)} />;
  }

  return (
    <div className="box-field" data-testid="facts-field" data-key={path}>
      <label htmlFor={id}>{field.label}</label>
      {control}
      {footer}
    </div>
  );
}

function HoursControl({
  field,
  days,
  problemsAt,
  onChange
}: {
  field: FactsField;
  days: DayInput[];
  problemsAt: ProblemsAt;
  onChange: (next: DayInput[]) => void;
}) {
  const id = useId();
  const hint = hintFor(field);
  const set = (index: number, change: Partial<DayInput>) =>
    onChange(days.map((day, at) => (at === index ? { ...day, ...change } : day)));

  return (
    <fieldset className="box-field" data-testid="facts-field" data-key={field.key} aria-describedby={`${id}-hint`}>
      <legend>{field.label}</legend>
      {days.map((day, index) => {
        const name = DAY_LABEL[day.day];
        const path = `${field.key}[${index}]`;
        return (
          <div key={day.day} data-testid="facts-day" data-day={day.day}>
            <div className="form-row">
              <strong>{name}</strong>
              <label className="box-check">
                <input
                  type="checkbox"
                  checked={day.closed}
                  onChange={(event) => set(index, { closed: event.target.checked })}
                />
                <span className="visually-hidden">{name}</span> Stängt
              </label>
              <div className="box-field">
                <label htmlFor={`${id}-${index}-from`}>
                  <span className="visually-hidden">{name}</span> Öppnar
                </label>
                <input
                  id={`${id}-${index}-from`}
                  type="time"
                  value={day.from}
                  disabled={day.closed}
                  onChange={(event) => set(index, { from: event.target.value })}
                />
              </div>
              <div className="box-field">
                <label htmlFor={`${id}-${index}-to`}>
                  <span className="visually-hidden">{name}</span> Stänger
                </label>
                <input
                  id={`${id}-${index}-to`}
                  type="time"
                  value={day.to}
                  disabled={day.closed}
                  onChange={(event) => set(index, { to: event.target.value })}
                />
              </div>
            </div>
            <ProblemLine id={`${id}-${index}-problem`} path={path} problems={problemsAt(path)} field={field} />
          </div>
        );
      })}
      <p className="line-hint" id={`${id}-hint`}>
        {hint}
      </p>
      <ProblemLine id={`${id}-problem`} path={field.key} problems={problemsAt(field.key)} field={field} />
    </fieldset>
  );
}

function ListControl({
  field,
  rows,
  problemsAt,
  onChange
}: {
  field: FactsField;
  rows: ListRow[];
  problemsAt: ProblemsAt;
  onChange: (next: ListRow[]) => void;
}) {
  const id = useId();
  const items = field.item_fields ?? [];
  const min = field.min_items ?? 0;
  const max = field.max_items ?? 30;

  return (
    <fieldset className="box-field" data-testid="facts-field" data-key={field.key} aria-describedby={`${id}-hint`}>
      <legend>{field.label}</legend>
      {rows.map((row, index) => (
        <fieldset key={index} className="box-field" data-testid="facts-list-row">
          <legend>Rad {index + 1}</legend>
          {items.map((item) => (
            <ValueControl
              key={item.key}
              field={item}
              input={row[item.key] ?? { kind: "scalar", text: "" }}
              path={`${field.key}[${index}].${item.key}`}
              problemsAt={problemsAt}
              onChange={(next) => onChange(rows.map((other, at) => (at === index ? { ...other, [item.key]: next } : other)))}
            />
          ))}
          <ProblemLine
            id={`${id}-${index}-problem`}
            path={`${field.key}[${index}]`}
            problems={problemsAt(`${field.key}[${index}]`)}
            field={field}
          />
          <div className="form-actions">
            <button
              type="button"
              className="button is-quiet"
              disabled={rows.length <= min}
              onClick={() => onChange(rows.filter((_row, at) => at !== index))}
            >
              Ta bort rad {index + 1}
            </button>
          </div>
        </fieldset>
      ))}
      <div className="form-actions">
        <button
          type="button"
          className="button"
          disabled={rows.length >= max}
          onClick={() => onChange([...rows, emptyRow(items)])}
        >
          Lägg till rad i {field.label}
        </button>
      </div>
      <p className="line-hint" id={`${id}-hint`}>
        {hintFor(field)}
      </p>
      <ProblemLine id={`${id}-problem`} path={field.key} problems={problemsAt(field.key)} field={field} />
    </fieldset>
  );
}

export function FieldControl({
  field,
  input,
  problemsAt,
  onChange
}: {
  field: FactsField;
  input: FieldInput;
  problemsAt: ProblemsAt;
  onChange: (next: FieldInput) => void;
}) {
  if (field.type === "opening_hours") {
    return (
      <HoursControl
        field={field}
        days={input.kind === "hours" ? input.days : []}
        problemsAt={problemsAt}
        onChange={(days) => onChange({ kind: "hours", days })}
      />
    );
  }
  if (field.type === "list") {
    return (
      <ListControl
        field={field}
        rows={input.kind === "list" ? input.rows : []}
        problemsAt={problemsAt}
        onChange={(rows) => onChange({ kind: "list", rows })}
      />
    );
  }
  return (
    <ValueControl
      field={field}
      input={input.kind === "scalar" || input.kind === "price" ? input : { kind: "scalar", text: "" }}
      path={field.key}
      problemsAt={problemsAt}
      onChange={onChange}
    />
  );
}
